// The provider dashboard's API client — `yoreflix/v1/provider/*`.
//
// ★★ EVERY CALL IS A GET, AND NONE OF THEM NAMES AN ORGANIZATION. The server resolves whose data
// this is from the authenticated session's user row (`OrgScope`), never from anything sent from
// here. That is not an implementation detail the client happens to respect — it is why this file
// has no org parameter to pass, and adding one would be the bug.
//
// ★★ THE ONLY PARAMETER ANY CALL TAKES IS `period`, and it is a KEY from a closed set the server
// serves. The window (calendar quarter, UTC-bounded) is resolved server-side, because a client
// computing its own boundaries would be a second, silently diverging definition of the unit the
// business accounts in.
//
// ⚠ The portal is READ-ONLY as a security posture, not as a feature list. There is no write path
// to design, so there is no write path to get wrong.

import { authClient } from '@yoreflix/core/api/authClient'

export interface OrgContact {
  Name?: string
  Email?: string
  JobTitle?: string
  Primary?: boolean
}

export interface ProviderOrganization {
  PrimaryKey: string
  Name: string
  Website: string
  Status: string
  Contacts: OrgContact[]
}

export interface ProviderAccount {
  EmailAddress: string
  DisplayName: string
}

/**
 * ⚠ THE TWO COUNTS ARE NOT RECONCILED, deliberately. `Supplied` counts what a supplier sent us;
 * `Catalogue` counts what of theirs is published. One delivery can become seven episodes, and a
 * master + captions + artwork are three deliveries of one title — two answers to two questions,
 * shown side by side and never subtracted from one another.
 */
export interface ProviderProfile {
  Linked: boolean
  Reason?: string
  Organization: ProviderOrganization | null
  Accounts?: ProviderAccount[]
  Catalogue?: { Titles: number; ByType: { EntryType: string | null; Titles: number }[] }
  Supplied?: { Deliveries: number; Items: number }
}

export interface PeriodOption {
  Key: string
  Label: string
}

export interface ProviderOverview {
  Linked: boolean
  Reason?: string
  Period?: PeriodOption
  Yours?: { Views: number; WatchSeconds: number; TitlesWatched: number; TitlesInCatalogue: number }
  /** ★ SCALARS ONLY — never a list, never another supplier's name (plan D3). */
  Platform?: { Views: number; WatchSeconds: number }
  Share?: { WatchTimePercent: number; ViewsPercent: number }
  PingSeconds?: number
}

export interface TitleRow {
  ContentKey: string
  Title: string
  EntryType: string
  /** The series/season this row sits under — empty for a top-level row. */
  ParentKey: string
  Depth: number
  /**
   * ★ A Series or Season: never watched itself, so its figures are the SUM of the episodes
   * beneath it (David: "both are purely roll-up points for aggregating episode viewing state").
   */
  IsRollUp: boolean
  Views: number
  WatchSeconds: number
  ShareOfYoursPercent: number
  /** ⚠ null = unranked (nothing watched in this period). Not the same as ranked last. */
  Rank: number | null
  RankOf: number
  /** What the rank is AGAINST: 'titles' | 'series' | 'in this series'. */
  RankScope: string
}

export interface DayPoint {
  /** YYYYMMDD. */
  Day: number
  Views: number
  WatchSeconds: number
  Hours: { Hour: number; WatchSeconds: number }[]
}

export interface TitleDetail {
  Linked: boolean
  Found: boolean
  ContentKey?: string
  Title?: string
  EntryType?: string
  IsRollUp?: boolean
  Period?: PeriodOption
  /** ⚠ EVERY day in the period, including the empty ones — the gaps are the shape. */
  Days?: DayPoint[]
  Hours?: { Hour: number; Views: number; WatchSeconds: number }[]
}

export interface ProviderTitles {
  Linked: boolean
  Reason?: string
  Period?: PeriodOption
  Titles?: TitleRow[]
}

export interface SuppliedNode {
  PrimaryKey: string
  ParentKey: string
  Name: string
  Kind: string
  EntryType: string
  SortOrder: number
  /** The catalogue entry this delivery became, where an association has been made. */
  CatalogueTitle: string
}

export interface SuppliedFile {
  PrimaryKey: string
  Bucket: string
  Label: string
  Name: string
  MediaType: string
  UploadedDayInt: number
}

/** ⚠ NO ANALYTICS COLUMNS, ever — see the partition note in the Supplied screen. */
export interface ProviderSupplied {
  Linked: boolean
  Reason?: string
  Nodes?: SuppliedNode[]
  Files?: Record<string, SuppliedFile[]>
}

export const providerClient = {
  /**
   * ⚠ "Not linked to an organization" arrives as a SUCCESS with `Linked: false`, not as a 403 or
   * an empty dataset. An account that received zeros would be told, in the portal's own voice,
   * that nobody watched anything of theirs.
   */
  profile(): Promise<ProviderProfile> {
    return authClient.request<ProviderProfile>('GET', 'provider/profile')
  },

  periods(): Promise<{ Periods: PeriodOption[]; Default: string }> {
    return authClient.request<{ Periods: PeriodOption[]; Default: string }>('GET', 'provider/periods')
  },

  overview(period: string): Promise<ProviderOverview> {
    return authClient.request<ProviderOverview>('GET', `provider/overview?period=${encodeURIComponent(period)}`)
  },

  titles(period: string): Promise<ProviderTitles> {
    return authClient.request<ProviderTitles>('GET', `provider/titles?period=${encodeURIComponent(period)}`)
  },

  /** One title's day-by-day and hour-by-hour shape. A series charts its whole subtree. */
  title(key: string, period: string): Promise<TitleDetail> {
    return authClient.request<TitleDetail>('GET', `provider/title/${encodeURIComponent(key)}?period=${encodeURIComponent(period)}`)
  },

  supplied(): Promise<ProviderSupplied> {
    return authClient.request<ProviderSupplied>('GET', 'provider/supplied')
  },
}

/**
 * Watch time for humans.
 *
 * ⚠ NEVER to the second. Seconds are pings × 3 and pings are not perfectly periodic, so the
 * figure is approximate by construction — printing "2,124 seconds" would imply a precision the
 * data does not have. Hours and minutes say what is actually known.
 */
export function watchTime(seconds: number): string {
  if (seconds <= 0) return '—'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes} min`
  return 'under a minute'
}
